import React, { useMemo } from 'react';
import { useAuth } from './auth';
import { getAdminAppUrl, getDealerAppUrl } from '~/helpers/utilHelper';
import Message from '~/model/message';

export const perms = {
  view_notaries: 'view_notaries',
  edit_notaries: 'edit_notaries',
  view_notary_bids: 'view_notary_bids',
  view_orders: 'view_orders',
  view_message_channel: 'view_message_channel',
  view_accounting: 'view_accounting',
  list_notifications: 'list_notification',
  view_notification: 'view_notification',
  view_terms: 'view_terms',
  view_messages: 'view_messages',
};

type AccessContextValue = {
  iAmGranted: (attribute: string, subject?: any) => boolean;
  iAmNotGranted: (attribute: string, subject?: any) => boolean;
  iAmGrantedAny: (attributes: string[], subject?: any) => boolean;
  iAmNotaryType: () => boolean;
  getUserAppUrl: (fragment: string) => string | undefined;
};

const AccessContext = React.createContext<AccessContextValue>({} as AccessContextValue);

const AccessProvider = (props: any) => {
  const { user: authUser } = useAuth();

  // all function definitions must live inside the useMemo callback so they are only
  // recreated when authUser changes — defining them outside defeats memoization
  const contextValue = useMemo(() => {

    const iAmAdminType = () => !!authUser?.isAdminType();
    const iAmDealerType = () => !!authUser?.isDealerType();
    const iAmNotaryType = () => !!authUser?.isNotaryType();

    const iAmGranted = (attribute: string, subject?: any) => {
      switch (attribute) {
        case perms.view_notaries:
        case perms.edit_notaries:
        case perms.view_notary_bids:
        case perms.view_orders:
        case perms.view_accounting:
        case perms.view_terms:
        case perms.view_messages:
          return true;
        case perms.view_message_channel:
          switch (parseInt(subject)) {
            case Message.CHANNEL_NOTARY_CUSTOMER:
            case Message.CHANNEL_SCHEDULER_NOTARY:
            case Message.CHANNEL_DEALER_SCHEDULER_NOTARY:
              return true;
          }
          return false;
        default:
          return false;
      }
    };

    const iAmNotGranted = (attribute: string, subject?: any) => !iAmGranted(attribute, subject);

    const iAmGrantedAny = (attributes: string[], subject?: any) => attributes.some(attr => iAmGranted(attr, subject));

    const getUserAppUrl = (fragment: string): string | undefined => {
      if (iAmAdminType()) {
        return getAdminAppUrl(fragment);
      } else if (iAmDealerType()) {
        return getDealerAppUrl(fragment);
      }
      return undefined;
    };

    return { iAmGranted, iAmNotGranted, iAmGrantedAny, iAmNotaryType, getUserAppUrl };

  }, [authUser]);

  return <AccessContext.Provider value={contextValue} {...props} />;
};

// helper hook that makes context data available
export const useAccess = () => {
  const context = React.useContext(AccessContext);
  if (context === undefined) {
    throw new Error('useAccess must be used within an AccessProvider');
  }
  return context;
};

export default AccessProvider;

